/**
 * @file ToastContext.tsx
 * @description Adapter that bridges the legacy `useToast / showToast` API
 * used by LoginPage and ChangePasswordPage to the new Toast system
 * in components/ui/Toast.tsx.
 *
 * The existing pages call:
 *   const { showToast } = useToast();
 *   showToast('success', 'Welcome back!');
 *
 * This file re-exports a compatible hook so those pages work unchanged.
 */
import { createContext, useContext, type ReactNode } from 'react';
import { useToast as useNewToast } from '../components/ui/Toast';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastContextValue {
  showToast: (type: ToastType, message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/**
 * ToastProvider (legacy adapter)
 * Wraps children and provides the showToast API.
 * NOTE: Must be rendered inside the new <ToastProvider> from ui/Toast.tsx.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const { toast } = useNewToast();

  const showToast = (type: ToastType, message: string) => {
    toast(message, type);
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
    </ToastContext.Provider>
  );
}

/**
 * useToast (legacy API)
 * Returns { showToast } compatible with existing LoginPage / ChangePasswordPage.
 */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}